import { EVENT_TYPES, type EventType } from './constants'

interface WaveTime {
  startTimestamp: number
  quantity: number
  allowSalesMinutesPastStartTime?: number
}

interface EventBase {
  id: string
  title?: string
  name?: string
  start?: number
  startDate?: string
  endDate?: string
  endTime?: string
  onSaleDate?: string
  hideDate?: string
  onSale?: string | boolean | number
  waveTimes?: WaveTime[]
  externalId?: string
  sefUrl?: string
  smallPic?: string
  largePic?: string
  imageUrl?: string
  [key: string]: unknown
}

interface LocationDateRange {
  city: string
  earliest: Date
  latest: Date
}

interface SeriesGroup {
  seriesKey: string
  baseName: string
  events: EventBase[]
  earliestDate: Date | null
  latestDate: Date | null
  locationDateRanges: LocationDateRange[]
}

interface SeriesResult {
  series: (EventBase & { isSeries: true; count: number })[]
  singles: (EventBase & { isSeries: false })[]
}

export const getEventType = (event: EventBase): EventType => {
  if (event.waveTimes && event.waveTimes.length > 0) {
    return EVENT_TYPES.TIMED_TICKETING
  }
  return EVENT_TYPES.GENERAL_ADMISSION
}

export const isEventOnSale = (event: EventBase | null): boolean => {
  if (!event) return false
  const now = new Date()
  const onSaleDate = event.onSaleDate ? new Date(event.onSaleDate) : null
  const hideDate = event.hideDate ? new Date(event.hideDate) : null
  if (onSaleDate && now < onSaleDate) return false
  if (hideDate && now > hideDate) return false
  return event.onSale === 'true' || event.onSale === true || event.onSale === 1
}

export const hasEventEnded = (event: EventBase | null): boolean => {
  if (!event || !event.endDate) return false
  const now = new Date()
  const endDateTime = new Date(`${event.endDate} ${event.endTime || '23:59:59'}`)
  return now > endDateTime
}

export const getEventStatus = (event: EventBase): { label: string; color: string } => {
  if (hasEventEnded(event)) return { label: 'Ended', color: 'gray' }
  if (!isEventOnSale(event)) return { label: 'Not On Sale', color: 'yellow' }
  return { label: 'On Sale', color: 'green' }
}

export const isWaveTimeAvailable = (waveTime: WaveTime | null): boolean => {
  if (!waveTime) return false
  const now = new Date()
  const startTime = new Date(waveTime.startTimestamp * 1000)
  const allowedMinutesPast = waveTime.allowSalesMinutesPastStartTime || 0
  const cutoffTime = new Date(startTime.getTime() + allowedMinutesPast * 60000)
  if (now > cutoffTime) return false
  return waveTime.quantity > 0
}

export const groupWaveTimesByDate = (waveTimes: WaveTime[] | null | undefined): Record<string, WaveTime[]> => {
  if (!waveTimes || !Array.isArray(waveTimes)) return {}
  return waveTimes.reduce<Record<string, WaveTime[]>>((acc, waveTime) => {
    const date = new Date(waveTime.startTimestamp * 1000)
    const dateKey = date.toISOString().split('T')[0]
    if (!acc[dateKey]) acc[dateKey] = []
    acc[dateKey].push(waveTime)
    return acc
  }, {})
}

const baseNameCache = new Map<string, string>()

export const extractBaseEventName = (title: string | null | undefined): string => {
  if (!title) return ''
  if (baseNameCache.has(title)) return baseNameCache.get(title)!

  const datePatterns = [
    / - [A-Z][a-z]+\s+\d{1,2}(?:st|nd|rd|th)?(?:,?\s+\d{4})?(?:\s*-\s*[A-Za-z]+)?$/,
    / - \d{1,2}\/\d{1,2}(?:\/\d{2,4})?$/,
    / \(\d{1,2}\/\d{1,2}\)$/,
  ]

  let baseName = title.trim()
  for (const pattern of datePatterns) {
    baseName = baseName.replace(pattern, '').trim()
  }

  baseNameCache.set(title, baseName)
  return baseName
}

export const groupEventsBySeries = (events: EventBase[] | null | undefined): SeriesResult => {
  if (!events || !Array.isArray(events)) return { series: [], singles: [] }

  const seriesMap = new Map<string, SeriesGroup>()

  events.forEach(event => {
    const baseName = extractBaseEventName(event.title || event.name)
    const seriesKey = event.externalId || event.sefUrl || baseName

    if (!seriesMap.has(seriesKey)) {
      seriesMap.set(seriesKey, {
        seriesKey,
        baseName,
        events: [],
        earliestDate: null,
        latestDate: null,
        locationDateRanges: [],
      })
    }

    const series = seriesMap.get(seriesKey)!
    series.events.push(event)

    const eventDate = event.start
      ? new Date(event.start * 1000)
      : event.startDate
        ? new Date(event.startDate)
        : null

    if (eventDate) {
      if (!series.earliestDate || eventDate < series.earliestDate) series.earliestDate = eventDate
      if (!series.latestDate || eventDate > series.latestDate) series.latestDate = eventDate
    }
  })

  // Compute per-city date ranges for each series
  seriesMap.forEach(seriesData => {
    const cityMap = new Map<string, { earliest: Date; latest: Date }>()
    for (const event of seriesData.events) {
      const city = (event.venueCity as string) || ''
      if (!city) continue
      const eventDate = event.start
        ? new Date((event.start as number) * 1000)
        : event.startDate
          ? new Date(event.startDate as string)
          : null
      if (!eventDate) continue

      const existing = cityMap.get(city)
      if (!existing) {
        cityMap.set(city, { earliest: eventDate, latest: eventDate })
      } else {
        if (eventDate < existing.earliest) existing.earliest = eventDate
        if (eventDate > existing.latest) existing.latest = eventDate
      }
    }
    seriesData.locationDateRanges = Array.from(cityMap.entries())
      .map(([city, range]) => ({ city, earliest: range.earliest, latest: range.latest }))
      .sort((a, b) => a.earliest.getTime() - b.earliest.getTime())
  })

  const series: SeriesResult['series'] = []
  const singles: SeriesResult['singles'] = []

  seriesMap.forEach(seriesData => {
    if (seriesData.events.length > 1) {
      series.push({
        ...seriesData.events[0],
        ...seriesData,
        id: seriesData.seriesKey,
        title: seriesData.baseName,
        count: seriesData.events.length,
        isSeries: true as const,
      })
    } else {
      singles.push({
        ...seriesData.events[0],
        isSeries: false as const,
      })
    }
  })

  return { series, singles }
}
